import React, { useState, useRef, useEffect, useMemo, useId, useCallback } from 'react';
import Icon from '../Icon/Icon';
import Checkbox, { type CheckboxVariant } from '../Checkbox/Checkbox';
import { useOutsideClick } from '../../../hooks/useOutsideClick';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import { matchesSearchText } from '../../../utils/searchText';
import styles from './MultiSelectFilter.module.css';

export interface MultiSelectOption {
  value: string;
  label: string;
  searchText?: string;
  icon?: string;
  iconColor?: string;
  color?: string;
}

export interface MultiSelectOptionGroup {
  title: string;
  values: string[];
  onChange: (values: string[]) => void;
  options: MultiSelectOption[];
}

interface MultiSelectFilterProps {
  values?: string[];
  onChange?: (values: string[]) => void;
  options?: MultiSelectOption[];
  groups?: MultiSelectOptionGroup[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  wrapperClassName?: string;
  noResultsText?: string;
  extraContent?: (selectedValues: string[]) => React.ReactNode;
  hideChips?: boolean;
  showSelectionCount?: boolean;
  chipsInTrigger?: boolean;
  dropdownClassName?: string;
  clearButtonLabel?: string;
  checkboxVariant?: CheckboxVariant;
}

const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  values = [],
  onChange = () => {},
  options = [],
  groups,
  placeholder = 'Filtrar...',
  searchPlaceholder = 'Pesquisar',
  disabled = false,
  className,
  wrapperClassName,
  noResultsText = 'Nenhum resultado encontrado',
  extraContent,
  hideChips = false,
  showSelectionCount = true,
  chipsInTrigger = false,
  dropdownClassName,
  clearButtonLabel = 'Limpar seleção',
  checkboxVariant = 'classic',
}) => {
  const viewportMargin = 64;
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropdownOffsetX, setDropdownOffsetX] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Array<HTMLDivElement | null>>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const previousOpenRef = useRef(open);
  const previousQueryRef = useRef(query);
  const isGrouped = !!groups?.length;

  const filterOptions = useCallback((groupOptions: MultiSelectOption[]) => {
    if (!query.trim()) return groupOptions;
    return groupOptions.filter(
      (option) => matchesSearchText(option.searchText ?? option.label, query)
        || matchesSearchText(option.value, query),
    );
  }, [query]);

  const filteredGroups = useMemo(() => {
    if (!isGrouped || !groups) return [];
    return groups.map((group) => ({
      ...group,
      filteredOptions: filterOptions(group.options),
    }));
  }, [filterOptions, groups, isGrouped]);

  const selectedOptions = useMemo(() => {
    if (isGrouped && groups) {
      return groups.flatMap((group) => (
        group.options.filter((option) => group.values.includes(option.value))
      ));
    }
    return options.filter((option) => values.includes(option.value));
  }, [groups, isGrouped, options, values]);

  const selectedCount = useMemo(() => {
    if (isGrouped && groups) {
      return groups.reduce((total, group) => total + group.values.length, 0);
    }
    return values.length;
  }, [groups, isGrouped, values]);

  const filtered = useMemo(() => {
    if (isGrouped) {
      return filteredGroups.flatMap((group) => group.filteredOptions);
    }
    return filterOptions(options);
  }, [filterOptions, filteredGroups, isGrouped, options]);

  useOutsideClick(containerRef, useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []));

  useEffect(() => {
    const wasOpen = previousOpenRef.current;
    const previousQuery = previousQueryRef.current;

    previousOpenRef.current = open;
    previousQueryRef.current = query;

    if (!open || filtered.length === 0) {
      return;
    }

    if (wasOpen && previousQuery === query) {
      return;
    }

    if (document.activeElement === inputRef.current) {
      return;
    }

    const selectedIndex = filtered.findIndex((option) => {
      if (isGrouped && groups) {
        return groups.some((group) => group.values.includes(option.value));
      }
      return values.includes(option.value);
    });
    const nextFocusIndex = selectedIndex >= 0 ? selectedIndex : 0;
    const animationFrameId = requestAnimationFrame(() => {
      optionRefs.current[nextFocusIndex]?.focus();
    });

    return () => cancelAnimationFrame(animationFrameId);
  }, [filtered, groups, isGrouped, open, query, values]);

  useEffect(() => {
    if (!open) {
      setDropdownOffsetX(0);
      return;
    }

    const updateDropdownOffset = () => {
      const containerRect = containerRef.current?.getBoundingClientRect();
      const dropdownWidth = dropdownRef.current?.offsetWidth ?? 222;

      if (!containerRect) {
        return;
      }

      let nextOffset = 0;
      const rightEdge = containerRect.left + dropdownWidth;

      if (rightEdge > window.innerWidth - viewportMargin) {
        nextOffset = window.innerWidth - viewportMargin - dropdownWidth - containerRect.left;
      }

      if (containerRect.left + nextOffset < viewportMargin) {
        nextOffset = viewportMargin - containerRect.left;
      }

      setDropdownOffsetX(nextOffset);
    };

    updateDropdownOffset();
    window.addEventListener('resize', updateDropdownOffset);

    return () => window.removeEventListener('resize', updateDropdownOffset);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const animationFrameId = requestAnimationFrame(() => {
      inputRef.current?.focus();
    });

    return () => cancelAnimationFrame(animationFrameId);
  }, [open]);

  const handleToggle = useCallback(
    (opt: MultiSelectOption, group?: MultiSelectOptionGroup) => {
      if (group) {
        if (group.values.includes(opt.value)) {
          group.onChange(group.values.filter((value) => value !== opt.value));
        } else {
          group.onChange([...group.values, opt.value]);
        }
      } else if (values.includes(opt.value)) {
        onChange(values.filter((value) => value !== opt.value));
      } else {
        onChange([...values, opt.value]);
      }
      setQuery('');
    },
    [onChange, values]
  );

  const handleRemove = useCallback(
    (val: string) => {
      if (isGrouped && groups) {
        const targetGroup = groups.find((group) => group.values.includes(val));
        if (targetGroup) {
          targetGroup.onChange(targetGroup.values.filter((value) => value !== val));
        }
        return;
      }
      onChange(values.filter((value) => value !== val));
    },
    [groups, isGrouped, onChange, values]
  );

  const handleClearAll = useCallback(() => {
    if (isGrouped && groups) {
      groups.forEach((group) => group.onChange([]));
      return;
    }
    onChange([]);
  }, [groups, isGrouped, onChange]);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setQuery('');
    requestAnimationFrame(() => {
      triggerRef.current?.focus();
    });
  }, []);
  useEscapeToClose(closeDropdown, open);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeDropdown();
      }
    },
    [closeDropdown]
  );

  const handleOptionKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    if (filtered.length === 0) {
      return;
    }

    const focusOption = (targetIndex: number) => {
      optionRefs.current[targetIndex]?.focus();
    };

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusOption((index + 1) % filtered.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusOption((index - 1 + filtered.length) % filtered.length);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      focusOption(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      focusOption(filtered.length - 1);
    }
  }, [filtered]);
  const renderOption = (
    opt: MultiSelectOption,
    index: number,
    group?: MultiSelectOptionGroup,
  ) => {
    const selectedValues = group?.values ?? values;
    const isSelected = selectedValues.includes(opt.value);

    return (
      <li key={`${group?.title ?? 'default'}-${opt.value}`} role="presentation">
        <Checkbox
          checked={isSelected}
          onPress={() => handleToggle(opt, group)}
          compact
          variant={checkboxVariant}
          borderless
          ariaLabel={opt.label}
          className={`${styles.option} ${isSelected ? styles.optionSelected : ''}`}
          onKeyDown={(event) => handleOptionKeyDown(event, index)}
          ref={(element) => {
            optionRefs.current[index] = element;
          }}
          label={(
            <span className={styles.optionContent}>
              {opt.icon && <Icon name={opt.icon} size={14} color={opt.iconColor} filled />}
              {opt.color && (
                <span
                  className={styles.colorDot}
                  style={{ backgroundColor: opt.color }}
                />
              )}
              <span className={styles.optionLabel}>{opt.label}</span>
            </span>
          )}
        />
      </li>
    );
  };

  const triggerAriaLabel = selectedOptions.length > 0
    ? `${placeholder}: ${selectedOptions.map((option) => option.label).join(', ')}`
    : placeholder;

  const triggerCls = [
    styles.trigger,
    open ? styles.triggerOpen : '',
    disabled ? styles.triggerDisabled : '',
    chipsInTrigger ? styles.triggerWithChips : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const wrapperCls = [
    styles.wrapper,
    chipsInTrigger ? styles.wrapperWithChips : '',
    wrapperClassName ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const dropdownCls = [styles.dropdown, dropdownClassName ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperCls} ref={containerRef} onKeyDown={handleKeyDown}>
      <button
        id={id}
        type="button"
        ref={triggerRef}
        className={triggerCls}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={(event) => {
          if (disabled) {
            return;
          }

          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={triggerAriaLabel}
        style={{textAlign: 'start'}}
      >
        {chipsInTrigger ? (
          selectedOptions.length > 0 ? (
            <span className={styles.triggerChips}>
              {selectedOptions.map((opt) => (
                <span key={opt.value} className={styles.chip}>
                  {opt.icon && <Icon name={opt.icon} size={14} color={opt.iconColor} filled />}
                  {opt.label}
                  <span
                    role="button"
                    className={styles.chipRemove}
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      handleRemove(opt.value);
                    }}
                    onMouseDown={(event) => {
                      event.preventDefault();
                    }}
                    aria-label={`Remover ${opt.label}`}
                    tabIndex={-1}
                  >
                    <Icon name="close" size={8} />
                  </span>
                </span>
              ))}
            </span>
          ) : (
            <span className={styles.triggerPlaceholder}>{placeholder}</span>
          )
        ) : (
          <span className={selectedCount > 0 ? styles.triggerValue : styles.triggerPlaceholder}>
            {selectedCount > 0 && showSelectionCount ? `${selectedOptions.length} selecionado(s)` : placeholder}
          </span>
        )}
        <span
          className={`${styles.chevron} ${open ? styles.chevronUp : ''} material-symbols-outlined`}
          aria-hidden="true"
        >
          expand_more
        </span>
      </button>

      {!hideChips && !chipsInTrigger && selectedOptions.length > 0 && (
        <div className={styles.chips}>
          {selectedOptions.map((opt) => (
            <span key={opt.value} className={styles.chip}>
              {opt.icon && <Icon name={opt.icon} size={14} color={opt.iconColor} filled />}
              {opt.label}
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => handleRemove(opt.value)}
                aria-label={`Remover ${opt.label}`}
              >
                <Icon name="close" size={8} />
              </button>
            </span>
          ))}
        </div>
      )}

      {open && (
        <div
          ref={dropdownRef}
          className={dropdownCls}
          role="dialog"
          aria-labelledby={id}
          style={{ left: dropdownOffsetX }}
        >
          <div className={styles.searchWrapper}>
            <span className={`${styles.searchIcon} material-symbols-outlined`} aria-hidden="true">
              search
            </span>
            <input
              ref={inputRef}
              type="text"
              className={styles.searchInput}
              placeholder={searchPlaceholder}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            {query && (
              <button
                type="button"
                className={styles.clearBtn}
                onMouseDown={(event) => {
                  event.preventDefault();
                  setQuery('');
                }}
                aria-label="Limpar busca"
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
              </button>
            )}
          </div>

          <ul className={styles.list} role="listbox" aria-multiselectable="true">
            {filtered.length === 0 ? (
              <li className={styles.noResults}>{noResultsText}</li>
            ) : isGrouped ? (
              (() => {
                let optionIndex = 0;
                return filteredGroups.flatMap((group, groupIndex) => {
                  if (group.filteredOptions.length === 0) {
                    return [];
                  }

                  const titleItem = (
                    <li
                      key={`${group.title}-title`}
                      role="presentation"
                      className={groupIndex > 0 ? styles.groupSection : undefined}
                    >
                      <p className={styles.groupTitle}>{group.title}</p>
                    </li>
                  );

                  const optionItems = group.filteredOptions.map((opt) => {
                    const currentIndex = optionIndex;
                    optionIndex += 1;
                    return renderOption(opt, currentIndex, group);
                  });

                  return [titleItem, ...optionItems];
                });
              })()
            ) : (
              filtered.map((opt, index) => renderOption(opt, index))
            )}
          </ul>

          <div className={styles.separator} />

          <button
            type="button"
            className={styles.clearFiltersBtn}
            onClick={handleClearAll}
          >
            <span className={`${styles.clearFiltersIcon} material-symbols-outlined`}>filter_alt_off</span>
            {clearButtonLabel}
          </button>

          {extraContent && extraContent(isGrouped && groups
            ? groups.flatMap((group) => group.values)
            : values)}
        </div>
      )}
    </div>
  );
};

export default MultiSelectFilter;
